import axios from 'axios';
import { clarity } from 'react-microsoft-clarity';
import { NETWORK_ISSUES } from '../Constants/networkErrorCodes';
import { getUtmParams } from '../utils/utmUtils';

/**
 * Telemetria de problemas de conexão (offline, timeout, lentidão, falha de boot)
 * enviada para New Relic (PageAction), GA (event) e Clarity (event + tag).
 *
 * Por que uma fila: quando a rede cai, o envio pro GA/Clarity/New Relic também
 * falha — justamente o evento que mais interessa se perderia. Todo evento vai
 * pra uma fila no localStorage e só é despachado quando há evidência de que a
 * conexão voltou (evento `online` ou uma resposta de API bem-sucedida) e as
 * ferramentas do tenant já foram inicializadas. A fila sobrevive a reload.
 *
 * IMPORTANTE: este módulo precisa ser o PRIMEIRO import de `src/index.js`. Ele
 * instrumenta `axios.create`, e os services criam suas instâncias no momento
 * em que são importados — qualquer instância criada antes ficaria sem
 * telemetria. Por isso também não importa nada de `services/`.
 */

const QUEUE_STORAGE_KEY = 'networkTelemetryQueue';
const OFFLINE_SINCE_STORAGE_KEY = 'networkOfflineSince';
// Mesmas chaves de tenantService.js (não importado de propósito — ver acima)
const TENANT_ID_STORAGE_KEY = 'tenantId';
const TENANT_CONFIG_STORAGE_KEY = 'tenantConfig';

const MAX_QUEUE_SIZE = 50;
const DEDUPE_WINDOW_MS = 30 * 1000;
const SLOW_RESPONSE_THRESHOLD_MS = 8000;
const MAX_ATTR_LENGTH = 100;

const lastSeenByKey = new Map();

const truncate = (value) => String(value ?? '').slice(0, MAX_ATTR_LENGTH);

const readJson = (storage, key, fallback) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    // storage cheio/bloqueado — telemetria nunca pode quebrar o app
  }
};

let queue = readJson(localStorage, QUEUE_STORAGE_KEY, []);
if (!Array.isArray(queue)) queue = [];

const persistQueue = () => writeStorage(localStorage, QUEUE_STORAGE_KEY, queue.length ? queue : null);

// Troca segmentos que podem ser dados pessoais ou ids (CPF, e-mail, uuid) por
// ':id' — o endpoint vira dimensão agregável e não vaza PII para as ferramentas.
const sanitizePath = (pathname) => pathname
  .split('/')
  .map((segment) => (/^\d+$|^[0-9a-f-]{16,}$|@/i.test(segment) ? ':id' : segment))
  .join('/');

const getEndpoint = (config = {}) => {
  try {
    const url = new URL(axios.getUri({ baseURL: config.baseURL, url: config.url }), window.location.origin);
    return truncate(`${url.host}${sanitizePath(url.pathname)}`);
  } catch {
    return 'desconhecido';
  }
};

const getConnectionContext = () => {
  const connection = navigator.connection || {};
  return {
    online: navigator.onLine,
    effective_type: connection.effectiveType || 'unknown',
    downlink_mbps: typeof connection.downlink === 'number' ? connection.downlink : -1,
    rtt_ms: typeof connection.rtt === 'number' ? connection.rtt : -1,
    save_data: Boolean(connection.saveData)
  };
};

const getBaseContext = () => {
  const utms = getUtmParams();
  return {
    tenant_id: sessionStorage.getItem(TENANT_ID_STORAGE_KEY) || 'unknown',
    page_path: truncate(sanitizePath(window.location.pathname)),
    utm_source: utms.utm_source || '',
    utm_medium: utms.utm_medium || '',
    utm_campaign: utms.utm_campaign || '',
    ...getConnectionContext()
  };
};

/**
 * Registra um problema de conexão. O envio acontece depois, via flush.
 *
 * @param {object} issue - item de NETWORK_ISSUES
 * @param {Record<string, string|number|boolean>} [attrs] - atributos extras
 * @param {string} [dedupeKey] - eventos com a mesma chave em < 30s são ignorados
 */
export const trackNetworkIssue = (issue, attrs = {}, dedupeKey = '') => {
  try {
    const now = Date.now();
    const key = `${issue.code}|${dedupeKey}`;
    if (now - (lastSeenByKey.get(key) || 0) < DEDUPE_WINDOW_MS) return;
    lastSeenByKey.set(key, now);

    const sanitizedAttrs = Object.fromEntries(
      Object.entries(attrs).map(([k, v]) => [k, typeof v === 'string' ? truncate(v) : v])
    );

    queue.push({
      event: issue.event,
      attrs: {
        error_code: issue.code,
        error_type: issue.type,
        ...getBaseContext(),
        ...sanitizedAttrs
      },
      occurredAt: now
    });
    if (queue.length > MAX_QUEUE_SIZE) queue = queue.slice(-MAX_QUEUE_SIZE);
    persistQueue();
    flushNetworkTelemetry();
  } catch {
    // idem
  }
};

// Só despacha depois que as ferramentas que o tenant usa estão de pé; senão o
// evento sairia da fila e seria descartado por um GA/Clarity ainda não iniciado.
const sinksReady = () => {
  const tenantConfig = readJson(sessionStorage, TENANT_CONFIG_STORAGE_KEY, null);
  if (!tenantConfig) return false;
  const flags = tenantConfig.FEATURE_FLAGS || {};
  if (flags.GA_ID && !window.gtag) return false;
  if (flags.CLARITY_ID && !clarity.hasStarted()) return false;
  return true;
};

const dispatch = ({ event, attrs, occurredAt }) => {
  const payload = { ...attrs, queued_ms: Date.now() - occurredAt };

  try {
    window.newrelic?.addPageAction?.(event, payload);
  } catch {
    // idem
  }

  try {
    window.gtag?.('event', event, payload);
  } catch {
    // idem
  }

  try {
    if (clarity.hasStarted()) {
      clarity.setEvent(event);
      clarity.setTag('network_error_code', attrs.error_code);
      // Prioriza a gravação dessa sessão no Clarity
      clarity.upgrade(`network: ${attrs.error_code}`);
    }
  } catch {
    // idem
  }
};

export const flushNetworkTelemetry = () => {
  try {
    if (!queue.length || !navigator.onLine || !sinksReady()) return;
    const items = queue;
    queue = [];
    persistQueue();
    items.forEach(dispatch);
  } catch {
    // idem
  }
};

// --- Estado de conexão para a UI (ConnectionBanner / useConnectionStatus) ---
// 'online' | 'offline' | 'unstable'. "Instável" vem das próprias requisições
// (lentas ou sem resposta com o aparelho online), não só de navigator.connection,
// que não existe no Safari/iOS.
const UNSTABLE_WINDOW_MS = 60 * 1000;
const UNSTABLE_MIN_SIGNALS = 2;
const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g'];

let connectionSignals = [];
let connectionStatus = 'online';
let statusRecheckTimer = null;
const statusListeners = new Set();

const computeConnectionStatus = () => {
  if (!navigator.onLine) return 'offline';
  const now = Date.now();
  connectionSignals = connectionSignals.filter((timestamp) => now - timestamp < UNSTABLE_WINDOW_MS);
  if (connectionSignals.length >= UNSTABLE_MIN_SIGNALS) return 'unstable';
  if (SLOW_EFFECTIVE_TYPES.includes(navigator.connection?.effectiveType)) return 'unstable';
  return 'online';
};

const updateConnectionStatus = () => {
  const next = computeConnectionStatus();
  if (next !== connectionStatus) {
    connectionStatus = next;
    statusListeners.forEach((listener) => listener(next));
  }

  // Reavalia quando o sinal mais antigo sair da janela (volta de 'unstable' p/ 'online')
  clearTimeout(statusRecheckTimer);
  if (connectionSignals.length) {
    statusRecheckTimer = setTimeout(
      updateConnectionStatus,
      connectionSignals[0] + UNSTABLE_WINDOW_MS - Date.now() + 100
    );
  }
};

const recordConnectionSignal = () => {
  if (!navigator.onLine) return;
  connectionSignals.push(Date.now());
  updateConnectionStatus();
};

export const getConnectionStatus = () => connectionStatus;

export const subscribeConnectionStatus = (listener) => {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
};

const isTimeout = (error) => error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

const instrumentAxiosInstance = (instance) => {
  instance.interceptors.request.use((config) => {
    config.metadata = { ...config.metadata, startedAt: Date.now() };
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const startedAt = response.config?.metadata?.startedAt;
      const durationMs = startedAt ? Date.now() - startedAt : 0;
      if (durationMs > SLOW_RESPONSE_THRESHOLD_MS) {
        recordConnectionSignal();
        const endpoint = getEndpoint(response.config);
        trackNetworkIssue(NETWORK_ISSUES.SLOW_RESPONSE, {
          endpoint,
          method: (response.config.method || 'get').toUpperCase(),
          duration_ms: durationMs
        }, endpoint);
      }
      // Resposta ok = conexão funcionando: bom momento pra esvaziar a fila
      flushNetworkTelemetry();
      return response;
    },
    (error) => {
      // Só falhas de rede: com `response` o servidor respondeu (4xx/5xx são
      // tratados por quem chamou e já aparecem no AjaxRequest do New Relic)
      if (!error?.response && !axios.isCancel(error) && error?.config) {
        const issue = !navigator.onLine
          ? NETWORK_ISSUES.OFFLINE
          : isTimeout(error) ? NETWORK_ISSUES.TIMEOUT : NETWORK_ISSUES.NETWORK_FAILURE;
        const startedAt = error.config.metadata?.startedAt;
        const endpoint = getEndpoint(error.config);
        recordConnectionSignal();
        trackNetworkIssue(issue, {
          endpoint,
          method: (error.config.method || 'get').toUpperCase(),
          duration_ms: startedAt ? Date.now() - startedAt : 0,
          timeout_ms: error.config.timeout || 0,
          axios_code: error.code || ''
        }, endpoint);
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const installAxiosInstrumentation = () => {
  if (axios.__networkTelemetryInstalled) return;
  axios.__networkTelemetryInstalled = true;

  instrumentAxiosInstance(axios);
  const originalCreate = axios.create.bind(axios);
  axios.create = (...args) => instrumentAxiosInstance(originalCreate(...args));
};

const installConnectivityListeners = () => {
  const reportOfflinePeriod = (endedBy) => {
    const offlineSince = Number(readJson(localStorage, OFFLINE_SINCE_STORAGE_KEY, 0));
    if (!offlineSince) return;
    writeStorage(localStorage, OFFLINE_SINCE_STORAGE_KEY, null);
    trackNetworkIssue(NETWORK_ISSUES.OFFLINE_PERIOD, {
      duration_ms: Date.now() - offlineSince,
      ended_by: endedBy // 'online' | 'reload' (página fechada/recarregada ainda offline)
    });
  };

  window.addEventListener('offline', () => {
    writeStorage(localStorage, OFFLINE_SINCE_STORAGE_KEY, String(Date.now()));
    updateConnectionStatus();
  });

  window.addEventListener('online', () => {
    // Sinais de antes da queda não dizem nada sobre a conexão nova
    connectionSignals = [];
    updateConnectionStatus();
    reportOfflinePeriod('online');
    flushNetworkTelemetry();
  });

  if (navigator.onLine) reportOfflinePeriod('reload');

  const connection = navigator.connection;
  connection?.addEventListener?.('change', () => {
    if (SLOW_EFFECTIVE_TYPES.includes(connection.effectiveType)) {
      trackNetworkIssue(NETWORK_ISSUES.DEGRADED);
    }
    updateConnectionStatus();
  });

  connectionStatus = computeConnectionStatus();
};

if (typeof window !== 'undefined') {
  installAxiosInstrumentation();
  installConnectivityListeners();
}
